import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import ProjectCarousel, { ProjectCarouselItem } from '../components/ProjectCarousel';
import gaiaPoweredHomepageScreenshot from '../assets/images/gaiapowered_homepage.png';
import TextType from '../components/TextType';
import ScrollFloat from '../components/ScrollFloatingText';
import StarBorder from '../components/StarBorder'

const Home = () => {
  const services = [
    {
      title: 'Web Development',
      description: 'Static, CRM-backed, or custom web solutions tailored to your needs.',
      icon: '🌐',
      link: '/services#web',
    },
    {
      title: 'Custom Software',
      description: 'Bespoke software development to solve your unique business challenges.',
      icon: '💻',
      link: '/services#custom',
    },
    {
      title: 'Cloud Solutions',
      description: 'Scalable cloud infrastructure ownership, ensuring your services are always available.',
      icon: '☁️',
      link: '/services#cloud',
    },
  ];

  const process = [
    {
      title: 'Discovery',
      description: 'We learn your business needs, challenges, and goals through in-depth consultation. Presenting a well-considered plan.',
      icon: '🔍',
    },
    {
      title: 'Development',
      description: 'We collaboratively build the solution to meet your needs, using industry best practices and cutting-edge technologies.',
      icon: '⚡',
    },
    {
      title: 'Launch',
      description: 'Launching your digital product and supporting your on it\'s service life.',
      icon: '🚀',
    }
  ];

  const testimonials = [
    {
      quote: "They [nautdigital] developed a clean, modern and customer focused website with integrations into HubSpot, speeding up the time it takes us to contact leads.",
      author: "Elliot Bradley",
      role: "Director",
      company: "Gaia Powered Limited"
    }
  ];

  // Project showcase data - customize these items to feature your portfolio
  // Each project should include: id, title, description, image URL, link, and optional tags
  const showcaseProjects: ProjectCarouselItem[] = [
    {
      id: 'gaiapowered',
      title: 'Gaia Powered',
      description: 'A B2C home renewables energy installer sought a compelling website, with an integrated quotation form into their CRM to facilitate high-volume sales.',
      image: gaiaPoweredHomepageScreenshot,
      link: 'https://gaiapowered.co.uk',
      tags: ['React', 'Node.js', 'Python', 'AWS']
    }
  ];

  // const techStack = [
  //   { name: 'React', icon: '⚛️' },
  //   { name: 'Node.js', icon: '🟢' },
  //   { name: 'Python', icon: '🐍' },
  //   { name: 'AWS', icon: '☁️' },
  //   { name: 'WordPress', icon: '📝' },
  //   { name: 'Docker', icon: '🐳' },
  // ];

  return (
    <>
      <Helmet>
        <title>nautdigital - Your digital partner</title>
        <meta name="description" content="We do it all. nautdigital is your digital partner, offering bespoke digital solutions that solve real world problems." />
      </Helmet>
      <div>
        {/* Hero Section */}
        <section className="relative h-screen flex items-center justify-center overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-t from-black to-dark-lighter z-0" />
          <div className="container mx-auto px-4 z-10 text-center">
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-5xl md:text-7xl font-bold text-white mb-6"
            > 
              Are your
              <span className="text-primary">
              <TextType 
                text={["website leads not converting?", 
                  "processes constrained?", 
                  "systems built for scale?", 
                  "workflows slowing you down?",
                  "teams stuck with manual tasks?",
                  "workflow tools working together?",
                  "ambitions limited by technical know-how?"]}
                typingSpeed={75}
                pauseDuration={2000}
                showCursor={true}
                cursorCharacter="|"
              />
              </span>
              <br />
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-xl text-gray-300 mb-6 max-w-3xl mx-auto"
            >
              Gain expert technology direction, allowing you to unlock additional sales, efficiencies & profit.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="flex items-center justify-center gap-4 mb-8 text-sm text-gray-400"
            >
              <div className="flex items-center gap-2">
                <span className="text-green-400">✓</span>
                <span>No cost</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-green-400">✓</span>
                <span>No obligation</span>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center w-full">
                <Link to="/technical-assessment" className="">
                <StarBorder
                  as="button"
                  className="btn"
                  color="white"
                  speed="5s"
                >
                  Claim a FREE assessment
                </StarBorder>
                </Link>
                <Link to="/services" className="btn btn-secondary text-lg px-8 py-4">
                  Our Services
                </Link>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Project Showcase Section */}
        <section className="section bg-dark-lighter">
          <div className="container">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center mb-12"
            >
              <h2 className="text-6xl font-bold text-accent mb-4">
              <ScrollFloat
                animationDuration={1}
                ease='back.inOut(2)'
                scrollStart='center bottom+=30%'
                scrollEnd='bottom bottom-=40%'
                stagger={0.03}
              >
                Recent Projects
              </ScrollFloat>
              </h2>
              <p className="text-xl text-gray-300 max-w-2xl mx-auto">
                Discover how we've helped others in your position.
              </p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <ProjectCarousel 
                projects={showcaseProjects}
                autoPlayInterval={6000}
                className="max-w-5xl mx-auto"
              />
            </motion.div>
          </div>
        </section>

        {/* Services Section */}
        <section className="section bg-dark">
          <div className="container">
            <h2 className="text-6xl font-bold text-accent text-center mb-12">
              Our Services
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {services.map((service, index) => (
                <motion.div
                  key={service.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.2 }}
                  className="relative bg-white/5 backdrop-blur-lg border-2 border-accent/20 shadow-2xl p-8 rounded-xl overflow-hidden hover:transform hover:scale-105 transition-all duration-300"
                >
                  {/* Diffused accent in top-right corner */}
                  <div className="absolute top-0 right-0 w-24 h-24 bg-accent/30 rounded-full blur-2xl opacity-70 -mt-8 -mr-8 pointer-events-none" />
                  <div className="text-4xl mb-4">{service.icon}</div>
                  <h3 className="text-2xl font-bold text-white mb-4">{service.title}</h3>
                  <p className="text-gray-400 mb-6">{service.description}</p>
                  <Link to={service.link} className="text-primary hover:text-primary-dark transition-colors">
                    Learn more →
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Process Section */}
        <section className="section bg-dark-lighter">
          <div className="container">
            <h2 className="text-6xl font-bold text-accent text-center mb-16">
              Our Process
            </h2>
            <div className="relative">
              {/* Desktop connecting line. Positioned to be in the middle of the icons. */}
              <div className="hidden md:block absolute top-14 left-0 w-full h-0.5 bg-accent/30 z-0"></div>

              <div className="relative flex flex-col md:flex-row justify-center items-start gap-y-12 md:gap-0 px-8">
                {process.map((step, index) => (
                  <motion.div
                    key={step.title}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.2 }}
                    className="flex-1 flex flex-col items-center text-center z-10"
                  >
                    <div className="bg-dark-lighter border-2 border-accent/50 rounded-full w-28 h-28 flex items-center justify-center text-4xl mb-6">
                      {step.icon}
                    </div>
                    <h3 className="text-xl font-bold text-white mb-2">{step.title}</h3>
                    <p className="text-gray-400 max-w-xs">{step.description}</p>
                  </motion.div>
                ))}
              </div>
            </div>
            <div className="text-center mt-16">
              <Link
                to="/process#process"
                className="inline-block px-8 py-4 bg-primary hover:bg-primary-dark text-white rounded-lg font-medium transition-all duration-200"
              >
                Learn More
              </Link>
            </div>
          </div>
        </section>

        {/* Tech Stack Section
        <section className="section bg-dark">
          <div className="container">
            <h2 className="text-4xl font-bold text-white text-center mb-12">
              Our Tech Stack
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-6 gap-8">
              {techStack.map((tech, index) => (
                <motion.div
                  key={tech.name}
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-dark-lighter p-6 rounded-xl text-center"
                >
                  <div className="text-4xl mb-2">{tech.icon}</div>
                  <p className="text-white font-medium">{tech.name}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section> */}

        {/* Testimonials Section */}
        <section className="section bg-dark-lighter">
          <div className="container">
            <h2 className="text-4xl font-bold text-accent text-center mb-12">
              Client Success Stories 
            </h2>
            <div className="grid grid-cols-6 gap-4">
              <div className="grid col-start-2 col-end-6">
              {testimonials.map((testimonial, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.2 }}
                  className="relative bg-dark-lighter/50 backdrop-blur-lg border-2 border-accent/20 shadow-2xl p-8 rounded-xl overflow-hidden"
                >
                  {/* Diffused accent in top-right corner */}
                  <div className="absolute top-0 right-0 w-24 h-24 bg-accent/30 rounded-full blur-2xl opacity-70 -mt-8 -mr-8 pointer-events-none" />
                  <p className="text-gray-300 text-lg mb-6">"{testimonial.quote}"</p>
                  <div>
                    <p className="text-white font-bold">{testimonial.author}</p>
                    <p className="text-gray-400">{testimonial.role}</p>
                    <p className="text-primary">{testimonial.company}</p>
                  </div>
                </motion.div>
              ))}</div>
            <div className="grid"></div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="section bg-dark">
          <div className="container text-center">
            <h2 className="text-4xl font-bold text-white mb-8">
              Ready to Transform Your Business?
            </h2>
            <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
              Let's discuss how we can help you achieve your digital goals.
            </p>
            <Link to="/contact" className="btn btn-primary text-lg px-8 py-3">
              Contact Us
            </Link>
          </div>
        </section>
      </div>
    </>
  );
};

export default Home; 